import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive"; // ← only one import needed
import TextHeader from "./TextHeader";
import { API_ROOT, webPath } from "../../apiconfig";

const sectionsLatest = [
  { title: "News", key: "newsData" },
];

const sectionsLeadership = [
  { title: "Featured Interview", key: "inteviewData" },
  { title: "Guest Posts", key: "guestPostData" },
];

const sectionsFeatures = [
  { title: "Articles", key: "articleData" },
];

const DropComp = React.memo(
  ({ className, section, handleSectionClick, maxItems }) => {
    const { cat_slug } = useParams();

    // ----- Break‑point detection -----
    const isMobile   = useMediaQuery({ query: "(max-width: 767px)" });
    const isLte1024  = useMediaQuery({ query: "(max-width: 1024px)" });
    const isLte768   = useMediaQuery({ query: "(max-width: 768px)" });

    // ----- Compute the maxWidth for the Leadership section -----
    const leadershipMaxWidth = useMemo(() => {
      if (isLte768)  return "650px";   // ≤ 768 px
      // if (isLte1024) return "800px";  
      // return "1000px";                  // > 1024 px (fallback / desktop)
    }, [isLte768, isLte1024]);

    // ----- State & effects (unchanged) -----
    const [headerData, setHeaderData] = useState({
      latest: {},
      leadership: {},
      features: {},
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeSection, setActiveSection] = useState(section);

    useEffect(() => {
      const fetchData = async () => {
        try {
          const [
            latestResponse,
            leadershipResponse,
            featuresResponse,
          ] = await Promise.all([
            fetch(`${API_ROOT}/api/post/latestPost`),
            fetch(`${API_ROOT}/api/post/leadership`),
            fetch(`${API_ROOT}/api/post/featured`),
          ]);

          const [
            latestData,
            leadershipData,
            featuresData,
          ] = await Promise.all([
            latestResponse.json(),
            leadershipResponse.json(),
            featuresResponse.json(),
          ]);

          setHeaderData({
            latest: latestData,
            leadership: leadershipData,
            features: featuresData,
          });
        } catch (e) {
          setError("Error fetching data");
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }, []);

    // ----- Rendering helpers (unchanged) -----
    const renderSection = useCallback(
      (sectionData, sections) => {
        return sections.map((sec, idx) => {
          const sectionUrl = `/topic/${sectionData[sec.key]?.[0]?.cat_slug || cat_slug}`;
          const isSpecialSection =
            activeSection === "Latest" || activeSection === "Features";
          const columnClass = isSpecialSection ? "col-md-12" : "col-md-6";
          const sectionClass = isMobile
            ? "col-12"
            : `col-12 ${columnClass} section-line `;

          return (
            <div key={idx} className={sectionClass}>
              {isMobile ? (
                <>
                  <a
                    href={sectionUrl}
                    style={{ textDecoration: "none", color: "black" }}
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = sectionUrl;
                    }}
                  >
                    <p className="section-title pt-1" style={{paddingLeft:"50px",paddingRight:"50px"}}>{sec.title}</p>
                  </a>
                  <p className="see-more mt-2 mb-2" style={{ display: "none" }}>
                    <a
                      className="latest-anchor"
                      style={{ textDecoration: "none", color: "black" }}
                      href={sectionUrl}
                    >
                      See More
                    </a>
                  </p>
                </>
              ) : (
                <>
                  <p className="section-title mt-1">{sec.title}</p>
                  {sectionData[sec.key]
                    ?.slice(0, maxItems)
                    .map((x, i) => (
                      <React.Fragment key={i}>
                        <a
                          href={`/${x.cat_slug}/${x.post_name}`}
                          style={{ textDecoration: "none", color: "black" }}
                        >
                          <TextHeader
                            alt={x.post_name}
                            headerImg={`${webPath}${x.banner_img}?width=300`}
                            headerText={x.post_title}
                            headerDesc={x.post_author}
                            post_date={x?.post_updated_date || x?.post_date}
                            headerDescClass="header-desc"
                          />
                        </a>
                        <p className="mt-2 section-line"></p>
                      </React.Fragment>
                    ))}
                  <p className="see-more mt-2 mb-2">
                    <a
                      className="latest-anchor"
                      style={{ textDecoration: "none", color: "black" }}
                      href={sectionUrl}
                    >
                      See More
                    </a>
                  </p>
                </>
              )}
            </div>
          );
        });
      },
      [maxItems, cat_slug, isMobile, activeSection]
    );

    const renderedSections = useMemo(() => {
      if (loading) return null;
      if (error) return <p style={{ color: "red" }}>Error: {error}</p>;

      switch (section) {
        case "Latest":
          return renderSection(headerData.latest, sectionsLatest);
        case "Leadership":
          return renderSection(headerData.leadership, sectionsLeadership);
        case "Features":
          return renderSection(headerData.features, sectionsFeatures);
        default:
          return null;
      }
    }, [headerData, section, renderSection, loading, error]);

    useEffect(() => {
      setActiveSection(section);
    }, [section]);

    // ----- FINAL RETURN – notice the dynamic maxWidth -----
    return (
      <div
        className={`container LatestData ${className}`}
        style={{
          zIndex: 1022,
          
          maxWidth:
            section === "Leadership"
              ? leadershipMaxWidth
              : "860px", 
          width: "100%",
          left: isMobile ? "0" : "50%",
          transform: isMobile ? "none" : "translateX(-50%)",
          marginLeft: isMobile ? "0" : "auto",
          marginRight: isMobile ? "0" : "auto",
        }}
      >
        <div className="row">{renderedSections}</div>
      </div>
    );
  }
);

export default DropComp;